import { useCallback } from 'react';
import { useQuery } from 'src/shared/lib/useQuery';
import { useAuth } from 'src/features/auth/contexts/AuthContext';
import { useOrg } from 'src/shared/contexts/OrgContext';
import { toTitleCase } from 'src/shared/lib/textFormat.utils';
import { Organisation } from 'src/shared/types/organisation.types';

export function useOrgs() {
  const { apiFetch } = useAuth();
  const { role } = useOrg();

  const select = useCallback(
    (data: Organisation[]) => {
      // If role is SALES, modify the organization names and subdomains
      if (role === 'SALES') {
        // Sort organizations alphabetically by industry
        const sortedOrgs = [...data].sort((a, b) => {
          const industryA = a.industry?.[0]?.toLowerCase() || 'acme';
          const industryB = b.industry?.[0]?.toLowerCase() || 'acme';
          return industryA.localeCompare(industryB);
        });

        // Keep track of company name occurrences
        const nameOccurrences: Record<string, number> = {};

        return sortedOrgs.map((org) => {
          const industry = org.industry?.[0] || 'Acme';
          const baseName = `${industry} company`;

          // Initialize counter if this is the first occurrence
          if (!nameOccurrences[industry]) {
            nameOccurrences[industry] = 1;
          } else {
            nameOccurrences[industry]++;
          }

          // Add number to name if it's not the first occurrence
          const displayName =
            nameOccurrences[industry] > 1 ? `${industry} company ${nameOccurrences[industry]}` : baseName;

          return {
            ...org,
            name: toTitleCase(displayName),
            subdomain: `${industry.toLowerCase()}.tekst.com`,
          };
        });
      }

      return data;
    },
    [role]
  );

  return useQuery({
    queryKey: ['orgs'],
    queryFn: getData,
    staleTime: 0,
    enabled: !!role,
    select,
  });

  async function getData() {
    const res = await apiFetch<Organisation[]>('/api/v2/organisations');
    if (!res.ok) {
      throw new Error('Something went wrong fetching orgs');
    }

    return await res.json();
  }
}
